/**
 * MathJax config for the Question Bank, including definitions for the Khan
 * macros MathJax does not ship.
 *
 * Lives outside QuestionBank.tsx so fast refresh keeps working there and this
 * stays unit-testable (see problem-text.ts for the same reasoning).
 */

/**
 * Khan's KaTeX fork defines colour macros MathJax does not have. The corpus is
 * normalized to plain `\textcolor` (question-scraper/scripts/normalize-latex.ts),
 * so these are a safety net for anything scraped since — without them MathJax's
 * `noundefined` package renders the macro name as red literal text.
 *
 * One-argument wrappers: `\blueE{5}` colours the 5. The previous definitions
 * declared 0 arguments as `{\color{blue}}`, closing the colour group immediately
 * and leaving every expression uncoloured.
 *
 * Does not cover `\overset{a}\blueD84`, where `\blueD` must collect its argument
 * after `\overset` took it as a bare token — no 1-arg macro can. The normalizer
 * handles that shape; it does not occur in the normalized corpus.
 */
export function buildKhanMacros(): Record<string, [string, number]> {
  const families: Record<string, string> = {
    blue: "blue",
    teal: "teal",
    green: "green",
    gold: "goldenrod",
    red: "red",
    maroon: "maroon",
    purple: "purple",
    mint: "mediumseagreen",
    gray: "gray",
    grey: "gray",
    pink: "hotpink",
    orange: "orange",
    kaBlue: "blue",
    kaGreen: "green",
  };

  const macros: Record<string, [string, number]> = {
    // mathtools' lap macros; MathJax ships the unprefixed forms.
    mathllap: ["\\llap{#1}", 1],
    mathrlap: ["\\rlap{#1}", 1],
    // Khan uses \bold as a one-token font switch.
    bold: ["\\mathbf{#1}", 1],
    degree: ["^\\circ", 0],
    infin: ["\\infty", 0],
    footnotesize: ["\\scriptsize", 0],
  };

  // Bare name plus every A–H tier, e.g. \blue, \blueA … \blueH.
  for (const [family, colour] of Object.entries(families)) {
    for (const tier of ["", "A", "B", "C", "D", "E", "F", "G", "H"]) {
      macros[family + tier] = [`\\textcolor{${colour}}{#1}`, 1];
    }
  }

  return macros;
}

export const MATHJAX_CONFIG = {
  loader: { load: ["input/tex", "output/chtml"] },
  tex: {
    inlineMath: [["$", "$"], ["\\(", "\\)"]],
    displayMath: [["$$", "$$"], ["\\[", "\\]"]],
    macros: buildKhanMacros(),
  },
};
